import Vue from 'vue';

const ChartingButton = Vue.extend({
  name: 'ChartingButton',
  inheritAttrs: false,
  props: {
    label: { type: String, default: '' },
    icon: { type: String, default: '' },
    severity: { type: String, default: 'primary' },
    outlined: Boolean,
    text: Boolean,
    rounded: Boolean,
    loading: Boolean,
    disabled: Boolean,
    size: { type: String, default: '' },
    as: { type: String, default: 'button' },
    to: { type: [String, Object], default: null }
  },
  render(h) {
    const tag = this.as === 'router-link' ? 'router-link' : this.as === 'a' ? 'a' : 'button';
    const children = [];
    if (this.loading) children.push(h('span', { class: 'charting-ui-spinner charting-ui-spinner--small' }));
    else if (this.icon) children.push(h('span', { class: this.icon, attrs: { 'aria-hidden': 'true' } }));
    if (this.label) children.push(h('span', { class: 'p-button-label' }, this.label));
    if (this.$slots.default) children.push(this.$slots.default);

    return h(tag, {
      class: [
        'p-button',
        `p-button-${this.severity}`,
        this.outlined && 'p-button-outlined',
        this.text && 'p-button-text',
        this.rounded && 'p-button-rounded',
        this.size && `p-button-${this.size}`
      ],
      attrs: {
        ...this.$attrs,
        disabled: this.disabled || this.loading,
        type: this.$attrs.type || (tag === 'button' ? 'button' : undefined)
      },
      props: tag === 'router-link' ? { to: this.to } : {},
      on: this.$listeners
    }, children);
  }
});

const ChartingTag = Vue.extend({
  name: 'ChartingTag',
  props: {
    value: { type: [String, Number], default: '' },
    severity: { type: String, default: 'secondary' }
  },
  render(h) {
    return h('span', { class: ['p-tag', `p-tag-${this.severity}`] }, this.$slots.default || [String(this.value)]);
  }
});

const ChartingProgressSpinner = Vue.extend({
  name: 'ChartingProgressSpinner',
  inheritAttrs: false,
  render(h) {
    return h('span', {
      class: 'charting-ui-spinner',
      style: this.$attrs.style,
      attrs: { role: 'progressbar', 'aria-label': 'Cargando' }
    });
  }
});

const createTextControl = tag => Vue.extend({
  name: tag === 'textarea' ? 'ChartingTextarea' : 'ChartingInputText',
  inheritAttrs: false,
  props: {
    value: { type: [String, Number], default: '' },
    modelValue: { type: [String, Number], default: undefined }
  },
  computed: {
    controlValue() {
      return this.modelValue === undefined ? this.value : this.modelValue;
    }
  },
  render(h) {
    return h(tag, {
      class: tag === 'textarea' ? 'p-inputtextarea p-inputtext' : 'p-inputtext',
      attrs: this.$attrs,
      domProps: { value: this.controlValue },
      on: {
        ...this.$listeners,
        input: event => {
          this.$emit('input', event.target.value);
          this.$emit('update:model-value', event.target.value);
        }
      }
    });
  }
});

const ChartingPopover = Vue.extend({
  name: 'ChartingPopover',
  data: () => ({
    visible: false,
    anchorElement: null,
    positionStyle: {
      position: 'fixed',
      top: '0px',
      left: '0px',
      visibility: 'hidden'
    }
  }),
  beforeDestroy() {
    this.removeGlobalListeners();
  },
  methods: {
    toggle(event) {
      if (this.visible) this.hide();
      else this.show(event);
    },
    show(event) {
      const candidate = event?.currentTarget || event?.target;
      this.anchorElement = candidate instanceof Element ? candidate : this.anchorElement;
      this.positionStyle = {
        position: 'fixed',
        top: '0px',
        left: '0px',
        visibility: 'hidden'
      };
      this.visible = true;
      this.$nextTick(() => {
        this.updatePosition();
        this.addGlobalListeners();
      });
    },
    hide({ restoreFocus = false } = {}) {
      const anchorElement = this.anchorElement;
      this.visible = false;
      this.removeGlobalListeners();
      if (restoreFocus && anchorElement?.focus) this.$nextTick(() => anchorElement.focus());
    },
    updatePosition() {
      if (!this.visible || !this.anchorElement?.getBoundingClientRect || !this.$el?.getBoundingClientRect) return;

      const viewportMargin = 8;
      const anchorGap = 8;
      const anchorRect = this.anchorElement.getBoundingClientRect();
      const popoverRect = this.$el.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      const popoverWidth = Math.min(popoverRect.width, Math.max(0, viewportWidth - viewportMargin * 2));
      const popoverHeight = Math.min(popoverRect.height, Math.max(0, viewportHeight - viewportMargin * 2));
      let left = anchorRect.left;
      let top = anchorRect.bottom + anchorGap;

      if (left + popoverWidth > viewportWidth - viewportMargin) left = anchorRect.right - popoverWidth;
      left = Math.max(viewportMargin, Math.min(left, viewportWidth - popoverWidth - viewportMargin));

      if (top + popoverHeight > viewportHeight - viewportMargin) top = anchorRect.top - popoverHeight - anchorGap;
      top = Math.max(viewportMargin, Math.min(top, viewportHeight - popoverHeight - viewportMargin));

      this.positionStyle = {
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        visibility: 'visible'
      };
    },
    onDocumentPointerDown(event) {
      if (!this.visible || this.$el?.contains(event.target) || this.anchorElement?.contains(event.target)) return;
      this.hide();
    },
    onDocumentKeydown(event) {
      if (!this.visible || event.key !== 'Escape') return;
      event.preventDefault();
      this.hide({ restoreFocus: true });
    },
    addGlobalListeners() {
      document.addEventListener('mousedown', this.onDocumentPointerDown, true);
      document.addEventListener('keydown', this.onDocumentKeydown);
      window.addEventListener('resize', this.updatePosition);
      window.addEventListener('scroll', this.updatePosition, true);
    },
    removeGlobalListeners() {
      document.removeEventListener('mousedown', this.onDocumentPointerDown, true);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      window.removeEventListener('resize', this.updatePosition);
      window.removeEventListener('scroll', this.updatePosition, true);
    }
  },
  render(h) {
    return this.visible
      ? h('div', { class: 'p-popover', style: this.positionStyle, attrs: { role: 'dialog' } }, this.$slots.default)
      : h();
  }
});

const ChartingDialog = Vue.extend({
  name: 'ChartingDialog',
  inheritAttrs: false,
  data: () => ({
    previouslyFocusedElement: null,
    previousBodyOverflow: '',
    bodyScrollLocked: false
  }),
  props: {
    visible: Boolean,
    modal: Boolean,
    dismissableMask: Boolean,
    closeOnEscape: Boolean,
    dialogStyle: {
      type: [String, Object, Array],
      default: null
    },
    dialogClass: { type: [String, Object, Array], default: null },
    maskClass: { type: [String, Object, Array], default: null }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    if (this.visible) this.openDialog();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    this.restorePageState();
  },
  watch: {
    visible(visible) {
      if (visible) this.openDialog();
      else this.closeDialog();
    }
  },
  methods: {
    close() { this.$emit('update:visible', false); },
    openDialog() {
      this.previouslyFocusedElement = document.activeElement;
      if (!this.bodyScrollLocked) {
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.bodyScrollLocked = true;
      }
      this.$emit('show');
      this.$nextTick(() => {
        const dialog = this.$el && this.$el.querySelector ? this.$el.querySelector('.p-dialog') : null;
        const focusTarget = dialog && dialog.querySelector('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (focusTarget) focusTarget.focus();
      });
    },
    closeDialog() {
      this.restorePageState();
      this.$emit('hide');
    },
    restorePageState() {
      if (this.bodyScrollLocked) {
        document.body.style.overflow = this.previousBodyOverflow;
        this.bodyScrollLocked = false;
      }
      if (this.previouslyFocusedElement && document.contains(this.previouslyFocusedElement)) {
        this.previouslyFocusedElement.focus();
      }
      this.previouslyFocusedElement = null;
    },
    onKeydown(event) {
      if (!this.visible) return;
      if (this.closeOnEscape && event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const dialog = this.$el && this.$el.querySelector ? this.$el.querySelector('.p-dialog') : null;
      const focusable = dialog ? [...dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')] : [];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    onMaskClick(event) {
      if (this.dismissableMask && event.target === event.currentTarget) this.close();
    }
  },
  render(h) {
    if (!this.visible) return h();
    return h('div', {
      class: ['p-dialog-mask', this.modal && 'p-component-overlay', this.maskClass],
      on: { click: this.onMaskClick }
    }, [
      h('section', {
        class: ['p-dialog', this.dialogClass],
        style: this.dialogStyle,
        attrs: { role: 'dialog', 'aria-modal': this.modal ? 'true' : 'false' }
      }, [
        h('header', { class: 'p-dialog-header' }, [
          h('div', { class: 'p-dialog-title' }, this.$slots.header),
          h('button', { class: 'p-dialog-header-close', attrs: { type: 'button', 'aria-label': 'Cerrar' }, on: { click: this.close } }, [h('span', { class: 'pi pi-times' })])
        ]),
        h('div', { class: 'p-dialog-content' }, this.$slots.default),
        this.$slots.footer ? h('footer', { class: 'p-dialog-footer' }, this.$slots.footer) : null
      ])
    ]);
  }
});

export function registerChartingUi() {
  Vue.component('Button', ChartingButton);
  Vue.component('Tag', ChartingTag);
  Vue.component('ProgressSpinner', ChartingProgressSpinner);
  Vue.component('InputText', createTextControl('input'));
  Vue.component('Textarea', createTextControl('textarea'));
  Vue.component('Popover', ChartingPopover);
  Vue.component('Dialog', ChartingDialog);
}
